/**
 * Pack presentation schema (D132) — what a shell shows AROUND the game: the
 * game's own mark, its subtitle, and the title screen's picture, theme, and
 * copy.
 *
 * @remarks
 * Until D132 every one of these lived in the player shell as a constant —
 * the title theme's URL, the hero's URL, "BLACKSITE: ORPHEUS PROTOCOL —
 * vertical slice", WAKE TERMINAL, BEGIN DESCENT, the two typed status lines
 * — which meant a second game booted wearing the first game's clothes. They
 * are the PACK's facts and they live in its manifest, beside the title they
 * decorate: the manifest is the part of a pack a game selector reads without
 * loading the world, and a title screen is exactly that kind of read.
 *
 * Every field is optional, and the loader fills the gaps from a built-in
 * table (the D25 response-table shape: a general default, a pack override,
 * one code path) — a pack that declares none of this still boots, wearing
 * plain labels and no picture. Absence is therefore honest rather than
 * broken: no `hero` means a title screen with no picture, no `theme` means a
 * silent title, no `logo` means the game's title set in type.
 *
 * `logo` and `hero` are pack-relative image paths rather than entries in the
 * plates table. A plate carries `alt` and `depicts` and answers to the D89
 * promise law because a player reads the room in it; a logo and a hero are
 * CHROME — decorative by rule, empty alt, nothing in them referable — so the
 * plates table's obligations would be lies about them. The theme, by
 * contrast, IS an audio asset like any bed, so it references the pack's audio
 * table by id and rides that table's existence and dead-content checks.
 *
 * What is deliberately NOT here: the shell's own strings ("ESTABLISHING
 * LINK…", "BOOT FAILED", "THE STORY HAS ENDED", "Begin again"). Those are the
 * engine speaking about the engine, not the pack speaking about its fiction;
 * a `strings` override table is the recorded next step if a pack ever needs
 * to re-voice them.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { audioIdSchema } from './identifiers.js';
import { nonBlankStringSchema } from './primitives.js';

/**
 * A pack-relative asset path for a piece of chrome. The same shape a plate's
 * `src` has; existence is the content gate's check, exactly as for plates.
 */
const chromeAssetPathSchema = nonBlankStringSchema;

/** The two typed status lines on the title screen — before and after the wake. */
export const titleScreenStatusSchema = z
	.strictObject({
		/** What the status line types before the wake gesture. */
		asleep: nonBlankStringSchema.optional(),
		/** What the status line types once the terminal is awake. */
		awake: nonBlankStringSchema.optional(),
		/**
		 * D179: what the status line types when a descent is already under way —
		 * a save exists with at least one turn in it. `{count}` is the number
		 * of locations walked, the MAP control's own number, so the two cannot
		 * disagree (Mike, 2026-09-15: "locations", never "rooms").
		 *
		 * D183: write it as a LABELED readout — `LOCATIONS WALKED: {count}`,
		 * the shape `help` already uses for `{directives}` — rather than as a
		 * counted noun. `{count} LOCATIONS WALKED` reads "1 LOCATIONS WALKED"
		 * on the first save anybody makes, and the fix does NOT belong in the
		 * engine: a `{s}` placeholder would be English pluralization compiled
		 * into a runtime that has to carry packs written in other languages.
		 */
		inProgress: nonBlankStringSchema.optional(),
	})
	.readonly();

/** The title-screen status pair — see {@link titleScreenStatusSchema}. */
export type TitleScreenStatus = z.infer<typeof titleScreenStatusSchema>;

/**
 * D179: the words on the start-over door and its confirm. Starting over is
 * the product's first destructive act, so it confirms first — the same
 * sentence `restart` uses in play, because it is the same act and should
 * not sound like two.
 */
export const titleScreenRestartSchema = z
	.strictObject({
		/**
		 * The confirm's sentence; `{count}` is the locations walked, offered
		 * but not spent by the default (D183 — a counted noun in a template
		 * reads "1 locations" the first time anybody restarts).
		 */
		confirm: nonBlankStringSchema.optional(),
		/** The button that keeps the descent. */
		keepLabel: nonBlankStringSchema.optional(),
		/** The button that throws it away — drawn in the danger color. */
		proceedLabel: nonBlankStringSchema.optional(),
	})
	.readonly();

export type TitleScreenRestart = z.infer<typeof titleScreenRestartSchema>;

/** Everything the title screen shows that is the pack's to say. */
export const titleScreenPresentationSchema = z
	.strictObject({
		/** The label on the second-step button (the one that enters the session). */
		beginLabel: nonBlankStringSchema.optional(),
		/** The full-bleed picture behind the title — chrome, never a plate. */
		hero: chromeAssetPathSchema.optional(),
		/** D179: the start-over door's confirm — see {@link titleScreenRestartSchema}. */
		restart: titleScreenRestartSchema.optional(),
		/** D179: the label on the start-over door, shown beside {@link resumeLabel} when a descent is under way. */
		restartLabel: nonBlankStringSchema.optional(),
		/** D179: the label on the second-step button when a descent is under way — it resumes rather than begins. */
		resumeLabel: nonBlankStringSchema.optional(),
		/** The typed status lines. */
		status: titleScreenStatusSchema.optional(),
		/**
		 * The theme that loops under the title once the terminal is awake — an
		 * id into the pack's `audio` table (D115), so it is declared once,
		 * exists on disk by the same gate as every bed, and cannot be dead.
		 */
		theme: audioIdSchema.optional(),
		/** The label on the first-step button (the audio gesture). */
		wakeLabel: nonBlankStringSchema.optional(),
	})
	.readonly();

/** The title-screen block — see {@link titleScreenPresentationSchema}. */
export type TitleScreenPresentation = z.infer<typeof titleScreenPresentationSchema>;

/**
 * How the map is drawn (D169, Mike's ruling 2026-09-10; D215, his ruling
 * of 2026-09-27): the builder's choice, between two. `basic` is the flat
 * grid of D34, the plainer chart. `scene` (D210) is the rooms in a real
 * three-dimensional space the player turns by hand — drawn by a renderer
 * the shell loads on first use, and drawn as the hologram where the browser
 * cannot draw it. The hologram — the extruded, floor-stacked map of D169,
 * one glowing material — is that stand-in and nothing else since D215: it
 * is never a builder's choice and never a button, so it is not in this
 * enum. A pack written against 0.4.0 that chose it is migrated onto `scene`,
 * which shows the same rooms and keeps the hologram behind them.
 * `tint` is the hologram's one color, as a hex triplet; absent, the brand's
 * signal green — a pack may wear its own (a Prime gold, an Andromeda blue)
 * as a fact about its fiction. `title` (D189) is what the map is CALLED —
 * the panel's heading, the figure's accessible name, the transcript render's
 * header; absent, "Map". The POC's is "Facility map", which was the engine's
 * only word for it until the second pack had no facility.
 *
 * `styles` (D209, Mike's two asks of 2026-09-25: the builder selects which
 * styles are available, and either allows the choice or locks it to one) is
 * the list the PLAYER may choose among, in the order a chooser shows them.
 * Absent, the map is locked to `style` — one map, no chooser, every pack
 * written before D209 unchanged. Two or more entries are the permission to
 * choose: the list IS the permission, so there is no second field that says
 * "locked" beside a list of three, and no state in which two of the three
 * are decorative. `style` stays the OPENING map: a pack may offer every
 * style and still open on the plan. Two rules the type cannot say — the
 * opening style, when declared, is among the offered; nothing is offered
 * twice — are refinements below.
 */
export const mapStyleSchema = z.enum(['basic', 'scene']);

export type MapStyle = z.infer<typeof mapStyleSchema>;

export const mapPresentationSchema = z
	.strictObject({
		style: mapStyleSchema.optional(),
		styles: z.array(mapStyleSchema).min(1).readonly().optional(),
		tint: z.string().regex(/^#[0-9a-fA-F]{6}$/u).optional(),
		title: nonBlankStringSchema.optional(),
	})
	.readonly()
	.refine((map) => map.styles === undefined || new Set(map.styles).size === map.styles.length, {
		message: 'map.styles lists a style twice — each offered style once, in the order the chooser shows them',
		path: ['styles'],
	})
	.refine((map) => map.style === undefined || map.styles === undefined || map.styles.includes(map.style), {
		message: 'map.style is the opening map, so it must be among map.styles — the player cannot open on a map they were not offered',
		path: ['style'],
	});

export type MapPresentation = z.infer<typeof mapPresentationSchema>;

/**
 * The SURFACE the world is dressed in (D203 named it `typeface` and moved
 * the face; D204 renamed it when the palette followed — Calder's §8.3 and
 * Mike's rulings of 2026-09-22 and 2026-09-25). `terminal` is the shell as
 * the POC wears it: near-black ground, the brand's green, the monospace
 * everywhere. `chart` is the island's: the chart's own sea for a ground, its
 * ochre for an accent, and the world's lines — the environment, found paper,
 * a declared speaker — in a serif, while the chrome (the typed command and
 * its echo, the shell's own lines, the status row, the map, every dialog)
 * stays monospace whatever the world wears.
 *
 * Named for the world it dresses, as `holo` names a facility rather than a
 * render technique, so a third that is neither has a name waiting. Two house
 * surfaces the shell ships is the whole power here — a pack never names a
 * font file or a color, because a pack that can point the player at a URL is
 * a security question rather than a design one, and a pack that can set the
 * ground under the chrome can make the chrome unreadable.
 */
export const surfaceSchema = z.enum(['chart', 'terminal']);

export type Surface = z.infer<typeof surfaceSchema>;

/** The pack's presentation block — see the module remarks. */
export const packPresentationSchema = z
	.strictObject({
		/**
		 * The game's own lockup: the hero on the title screen and the mark in
		 * the masthead. Absent, the game's `title` is set in type in both
		 * places (Mike's ruling, 2026-09-02: the name on the door is the
		 * GAME's, never the engine's).
		 */
		logo: chromeAssetPathSchema.optional(),
		/** How the map is drawn (D169); absent is the hologram in the brand's green. */
		map: mapPresentationSchema.optional(),
		/** The line under the mark in the masthead; absent means no line. */
		subtitle: nonBlankStringSchema.optional(),
		/** The surface the world is dressed in (D203, D204); absent is `terminal`, so a pack that says nothing is the POC's shape exactly. */
		surface: surfaceSchema.optional(),
		titleScreen: titleScreenPresentationSchema.optional(),
	})
	.readonly();

/** One pack presentation block — see {@link packPresentationSchema}. */
export type PackPresentation = z.infer<typeof packPresentationSchema>;
